import { useState } from "react";
import { format, parseISO, isValid } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";

import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * DatePicker — shadcn Popover + Calendar wrapper that speaks the same
 * ISO-yyyy-MM-dd shape as the native `<input type="date">` it replaces.
 *
 * value:    ISO string ("2026-04-01") or "" / null / undefined
 * onChange: called with an ISO string or "" when cleared
 */
export function DatePicker({
  value,
  onChange,
  disabled,
  placeholder = "Pick a date",
  className,
  "data-testid": testId,
  align = "start",
  clearable = true,
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const parsed = value ? parseISO(value) : null;
  const dateObj = parsed && isValid(parsed) ? parsed : null;

  const handleSelect = (d) => {
    if (!d) {
      onChange?.("");
    } else {
      // Format in local time so yyyy-MM-dd matches what the user picked
      // — parseISO on a bare date always returns local midnight.
      onChange?.(format(d, "yyyy-MM-dd"));
    }
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange?.("");
  };

  const displayFormat = compact ? "d MMM ''yy" : "d MMM yyyy";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full h-10 justify-start text-left font-normal rounded-lg",
            compact && "px-2",
            !dateObj && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className={cn("mr-2 h-4 w-4 flex-shrink-0", compact && "mr-1.5 h-3.5 w-3.5")} />
          <span className="flex-1 truncate">
            {dateObj ? format(dateObj, displayFormat) : placeholder}
          </span>
          {dateObj && clearable && !disabled && (
            <span
              role="button"
              aria-label="Clear date"
              tabIndex={0}
              onClick={clear}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && clear(e)}
              data-testid={testId ? `${testId}-clear` : undefined}
              className="ml-1 rounded-full p-0.5 hover:bg-muted transition-colors"
            >
              <X className="h-3.5 w-3.5 opacity-60 hover:opacity-100" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="single"
          selected={dateObj || undefined}
          defaultMonth={dateObj || undefined}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
